/**
 * Mock Control Utility
 *
 * This utility allows you to easily enable/disable API mocking for testing.
 * Mocks will ONLY run when explicitly enabled - never in normal development.
 */

import { worker } from './browser'

// Check if mocking should be enabled
const shouldEnableMocking = (): boolean => {
  // Enable mocking when:
  // 1. URL contains ?mock=true
  // 2. localStorage has 'enableMocks' set to 'true'
  // 3. Environment variable REACT_APP_ENABLE_MOCKS is 'true'

  const urlParams = new URLSearchParams(window.location.search)
  const mockParam = urlParams.get('mock')
  const localStorageMock = localStorage.getItem('enableMocks')
  const envMock = process.env.REACT_APP_ENABLE_MOCKS

  return (
    mockParam === 'true' || localStorageMock === 'true' || envMock === 'true'
  )
}

// Enable mocks manually
export const enableMocks = async (): Promise<void> => {
  localStorage.setItem('enableMocks', 'true')
  await worker.start({
    onUnhandledRequest: 'warn', // Only warn about unhandled requests
  })
  console.log('🎭 MSW mocking enabled')
}

// Disable mocks manually
export const disableMocks = (): void => {
  localStorage.removeItem('enableMocks')
  worker.stop()
  console.log('🔄 MSW mocking disabled - using real API')
}

// Check current mock status
export const isMockingEnabled = (): boolean => {
  return shouldEnableMocking()
}

// Auto-start mocks if conditions are met
export const initializeMocks = async (): Promise<void> => {
  if (shouldEnableMocking()) {
    await enableMocks()
  }
}

// Helper functions for testing
export const mockUtils = {
  enable: enableMocks,
  disable: disableMocks,
  isEnabled: isMockingEnabled,

  // Quick toggle for development
  toggle: async (): Promise<void> => {
    if (isMockingEnabled()) {
      disableMocks()
    } else {
      await enableMocks()
    }
  },
}

// Make it available globally for easy console access
if (typeof window !== 'undefined') {
  ;(window as any).mockUtils = mockUtils
}
